import { IMutations } from '@/models/store/mutations.interface';
import { DEFAULT_OVER_PERFORMANCE_LIMIT } from '@/static/default-values';
import { CampaignType, DeliveryType } from '@studyportals/campaign-management-api-interface';
import { ModalErrorStates } from '@/models/modal/modal.enum';
import redshiftTracker from '@/infrastructure/redshift-tracker';
import availableEntitiesPresenter from '@/presenters/available-entities-presenter';
import state from './state';

const mutations: IMutations = {
	addCustomProduct: (customProduct) => {
		state.customProducts.push(customProduct);
		state.customProducts = [...state.customProducts];
	},
	addIdOfModifiedCustomProduct: (id) => {
		state.idsOfModifiedCustomProducts.push(id);
		state.idsOfModifiedCustomProducts = [...state.idsOfModifiedCustomProducts];
	},
	addLinkedOrganisation: (organisation) => {
		state.linkedOrganisations.push(organisation);
		state.linkedOrganisations = [...state.linkedOrganisations];
	},
	addRightsUser: (user) => {
		state.rightsUsers.push(user);
		state.rightsUsers = [...state.rightsUsers];
	},
	deleteCustomProduct: (id) => {
		const index = state.customProducts.findIndex(
			(product) => product.id === id
		);

		if (index === -1) {
			return;
		}

		state.customProducts.splice(index, 1);
		state.customProducts = [...state.customProducts];
	},
	editCustomProduct: (customProduct) => {
		const index = state.customProducts.findIndex(
			(product) => product.id === customProduct.id
		);

		if (index === -1) {
			return;
		}

		state.customProducts[index] = customProduct;
		state.customProducts = [...state.customProducts];
	},
	incrementLoadCount: () => {
		state.loadCount++;
	},
	markGeotargetingMicroserviceAsReady: () => {
		state.isGeotargetingMicroserviceReady = true;
	},
	removeLinkedOrganisation: (organisation) => {
		state.linkedOrganisations = state.linkedOrganisations.filter(
			(linkedOrganisation) => linkedOrganisation.id !== organisation.id
		);
	},
	removeRightsUser: (user) => {
		state.rightsUsers = state.rightsUsers.filter(
			(rightsUser) => rightsUser.id !== user.id
		);
	},
	resetIdsOfModifiedCustomProducts: () => {
		state.idsOfModifiedCustomProducts = [];
	},
	resetValidationInfoDates: () => {
		state.validationInfoDates = {
			message: '',
			invalidTypes: []
		};
	},
	revertToOriginalCampaignDetails: () => {
		if (!state.originalCampaignData) {
			return;
		}

		const presentableEntities = availableEntitiesPresenter.convertToPresentableEntities(state.originalCampaignData.campaignListingEntities);
		const sortedEntities = availableEntitiesPresenter.sortPresentableEntities(presentableEntities);

		// All "baseCampaignData" properties are relevant except for "id".
		const amountOfBaseSettings = Object.keys(state.originalCampaignData.baseCampaignData).length - 1;
		// All properties are relevant except for "baseCampaignData".
		const amountOfOtherSettings = Object.keys(state.originalCampaignData).length - 1;
		const amountOfSettings = amountOfBaseSettings + amountOfOtherSettings;

		const revertMethods = [
			mutations.setName.bind(this, state.originalCampaignData.baseCampaignData.name),
			mutations.setStartDate.bind(this, state.originalCampaignData.baseCampaignData.startDate),
			mutations.setEndDate.bind(this, state.originalCampaignData.baseCampaignData.endDate),
			mutations.setPartnershipType.bind(this, state.originalCampaignData.baseCampaignData.partnershipType),
			mutations.setDeliveryModeAndLimit.bind(this, state.originalCampaignData.baseCampaignData.deliveryMode),
			mutations.setCampaignType.bind(this, state.originalCampaignData.baseCampaignData.campaignType),
			mutations.setCurrency.bind(this, state.originalCampaignData.baseCampaignData.currency),
			mutations.setBudgetListing.bind(this, state.originalCampaignData.baseCampaignData.listingBudget),
			mutations.setClientServicesPerson.bind(this, state.originalCampaignData.baseCampaignData.clientServicesPerson),
			mutations.setAccountManager.bind(this, state.originalCampaignData.baseCampaignData.accountManager),
			mutations.setCrmId.bind(this, state.originalCampaignData.baseCampaignData.crmId),
			mutations.setRegion.bind(this, state.originalCampaignData.baseCampaignData.region),
			mutations.setLinkedOrganisations.bind(this, state.originalCampaignData.baseCampaignData.linkedOrganisations),
			mutations.setBudgetCustom.bind(this, state.originalCampaignData.baseCampaignData.customProductsBudget),
			mutations.setCampaignLiftExcluded.bind(this, state.originalCampaignData.baseCampaignData.isExcludedFromCampaignLift),
			mutations.setCustomProducts.bind(this, state.originalCampaignData.customProducts),
			mutations.setRightsUsers.bind(this, state.originalCampaignData.clientsWithAccess),
			mutations.setPersistGLParamRefLink.bind(this, state.originalCampaignData.baseCampaignData.persistGLParamRefLink),
			mutations.setInternalNotes.bind(this, state.originalCampaignData.baseCampaignData.internalNotes),
			mutations.setAvailableEntities.bind(this, sortedEntities)
		];

		if (revertMethods.length !== amountOfSettings) {
			throw new Error('Not all settings are reverted to their original values.');
		}

		revertMethods.forEach((method) => method());
	},
	setAccountManager: (id) => {
		state.accountManager = id;
	},
	setAllAccountManagers: (accountManagers) => {
		state.allAccountManagers = accountManagers;
	},
	setAllClientServicesPeople: (clientServicesPeople) => {
		state.allClientServicesPeople = clientServicesPeople;
	},
	setAllPricingProducts: (products) => {
		state.allPricingProducts = products;
	},
	setAttachedStatusOfAllEntities: (attached) => {
		state.availableEntities.forEach((entity) => {
			entity.attached = attached;
		});

		state.availableEntities = [...state.availableEntities];
	},
	setAttachedStatusOfEntity: (id, attached) => {
		const relevantEntity = state.availableEntities.find(
			(entity) => entity.id === id
		);

		if (!relevantEntity) {
			return;
		}

		relevantEntity.attached = attached;
		state.availableEntities = [...state.availableEntities];
	},
	setAuditorEntries: (entries) => {
		state.auditorEntries = entries;
		state.auditorEntries = [...state.auditorEntries];
	},
	setAvailableEntities: (entities) => {
		state.availableEntities = entities;
		state.availableEntities = [...state.availableEntities];
	},
	setBudgetCustom: (budget) => {
		state.budgetCustom = budget;
	},
	setBudgetListing: (budget) => {
		state.budgetListing = budget;
	},
	setBudgetTotal: (budget) => {
		state.budgetTotal = budget;
	},
	setCampaignImportBannerDetails: (details) => {
		state.campaignImportBannerDetails = details;
	},
	setCampaignLiftExcluded: (value) => {
		state.campaignLiftExcluded = value;
	},
	setCampaignReadinessInfo: (info) => {
		state.campaignReadinessInfo = info;
	},
	setCampaignType: (type) => {
		if (!type) {
			return;
		}

		if (type === CampaignType.CUSTOM_PRODUCTS) {
			state.budgetListing = 0;
		}

		state.campaignType = type;
	},
	setClientServicesPerson: (id) => {
		state.clientServicesPerson = id;
	},
	setCrmId: (id) => {
		state.crmId = id;
	},
	setCurrency: (currency) => {
		state.currency = currency;
	},
	setCurrentRoute: (name, id) => {
		// Refresh the page to force data to be re-fetched for the new campaign ID.
		const idHasChanged = typeof id === 'number' && id !== 0 && id !== state.router.props.id;
		const idWasAlreadySetOrToolFinishedLoading = state.router.props.id !== 0 || state.loadCount !== 0;
		if (idHasChanged && idWasAlreadySetOrToolFinishedLoading && !state.router.props.isCampaignCreation) {
			window.location.reload();
			return;
		}

		if (!id && state.router.props.id) {
			id = state.router.props.id;
		}

		state.router.current = name;

		// Maintain a url format which uses a hash to separate the universityAdmin url part from the microservice url part.
		let url = `${window.location.origin}/campaign-editor/#/${name}`;
		if (id) {
			url += `/${id}/`;
		}

		// Keep the url in the address bar up-to-date.
		history.pushState({}, '', url);

		state.router.props.id = id ? id : 0;

		mutations.setEntitySearchQuery('');

		if (state.loadCount === 0) {
			return;
		}

		redshiftTracker.markTabSwitchStart();
	},
	setCustomProducts: (customProducts) => {
		state.customProducts = customProducts;
	},
	setDeliveryMode: (type) => {
		let limit = DEFAULT_OVER_PERFORMANCE_LIMIT;
		if (state.deliveryMode.type === DeliveryType.CUSTOM_OVERPERFORMANCE) {
			limit = state.deliveryMode.overperformanceLimit;
		}

		/* eslint-disable indent */
		switch (type) {
			case DeliveryType.EVENLY:
				state.deliveryMode = {
					type: DeliveryType.EVENLY
				};
				break;
			case DeliveryType.CUSTOM_OVERPERFORMANCE:
				state.deliveryMode = {
					type: DeliveryType.CUSTOM_OVERPERFORMANCE,
					overperformanceLimit: limit
				};
				break;
		}
		/* eslint-enable indent */
	},
	setDeliveryModeAndLimit: (mode) => {
		mutations.setDeliveryMode(mode.type);
		if (mode.type !== DeliveryType.CUSTOM_OVERPERFORMANCE) {
			return;
		}

		mutations.setOverPerformanceLimit(mode.overperformanceLimit);
	},
	setDuplicateEntryCampaignDetails: (details) => {
		state.duplicateEntryCampaignDetails = details;
	},
	setEndDate: (date) => {
		state.endDate = date;
	},
	setEntitiesAffectedByUnlinking: (entities) => {
		state.entitiesAffectedByUnlinking = entities;
	},
	setEntitySearchQuery: (query) => {
		state.entitySearchQuery = query;
	},
	setInternalNotes: (notes) => {
		/* Ensure TinyMCE-modified content is cleaned up so the changes extractor is able to detect relevant changes. Cleaned up parts are:
		- Line breaks
		- <br> tags */
		notes = notes
			.replace(/[\n\r]/g, "")
			.replace(/<br\s*\/?>/gi, "<br>");
		state.internalNotes = notes;
	},
	setInvalidCampaignPropertyDetails: (details) => {
		state.invalidCampaignPropertyDetails = details;
	},
	setImportCampaignConfigurationModalActive: (active) => {
		state.isImportCampaignConfigurationModalActive = active;
	},
	setIsCampaignCreation: (isCampaignCreation) => {
		state.router.props.isCampaignCreation = isCampaignCreation;
	},
	setLinkedOrganisations: (organisations) => {
		state.linkedOrganisations = organisations;
	},
	setLoadingStatusAccountManagers: (status) => {
		state.loadingStatusAccountManagers = status;
	},
	setLoadingStatusAuditorEntries: (status) => {
		state.loadingStatusAuditorEntries = status;
	},
	setLoadingStatusCampaignReadinessInfo: (status) => {
		state.loadingStatusCampaignReadinessInfo = status;
	},
	setLoadingStatusClientServicesPeople: (status) => {
		state.loadingStatusClientServicesPeople = status;
	},
	setLoadingStatusCustomProducts: (status) => {
		state.loadingStatusCustomProducts = status;
	},
	setLoadingStatusEntities: (status) => {
		state.loadingStatusEntities = status;
	},
	setLoadingStatusPricingProducts: (status) => {
		state.loadingStatusPricingProducts = status;
	},
	setLoadingStatusPrimaryData: (status) => {
		state.loadingStatusPrimaryData = status;
	},
	setLoadingStatusRights: (status) => {
		state.loadingStatusRights = status;
	},
	setModalCustomProductState: (status) => {
		state.modalCustomProductState = status;
		state.selectedTableActionsDropdown = -1;
	},
	setModalErrorState: (errorState) => {
		// If the active error relates to an unsupported campaign setting, don't overwrite it with something else.
		if (state.modalErrorState === ModalErrorStates.UNSUPPORTED_CAMPAIGN_SETTING) {
			return;
		}

		state.modalErrorState = errorState;
	},
	setModalUnsafeExitState: (status) => {
		state.modalUnsafeExitState = status;
	},
	setName: (name) => {
		state.name = name;
	},
	setNotificationStatus: (status) => {
		state.notificationStatus = status;
	},
	setNextRoute: (name) => {
		state.router.next = name;
	},
	setOriginalCampaignData: (data) => {
		state.originalCampaignData = data;
	},
	setOverlappingCampaignsCluster: (cluster) => {
		state.overlappingCampaignsCluster = cluster;
	},
	setOverPerformanceLimit: (limit) => {
		if (state.deliveryMode.type === DeliveryType.EVENLY) {
			return;
		}

		state.deliveryMode = {
			type: state.deliveryMode.type,
			overperformanceLimit: limit
		};
	},
	setPartnershipType: (type) => {
		state.partnershipType = type;
	},
	setPersistGLParamRefLink: (value) => {
		state.persistGLParamRefLink = value;
	},
	setRegion: (region) => {
		state.region = region;
	},
	setRightsUsers: (users) => {
		state.rightsUsers = [...users];
	},
	setSelectedCustomProductId: (id) => {
		state.selectedCustomProductId = id;
	},
	setSelectedTypeForCustomProductCreation: (type) => {
		state.selectedTypeForCustomProductCreation = type;
	},
	setSelectedTableActionsDropdown: (id) => {
		state.selectedTableActionsDropdown = id;
	},
	setStandAloneLogin: (standAloneLogin) => {
		state.standAloneLogin = standAloneLogin;
	},
	setStartDate: (date) => {
		state.startDate = date;
	},
	setValidationInfoDates: (info) => {
		state.validationInfoDates = info;
	},
	setValidationResult: (result) => {
		state.validationResult = result;
	},
	setVisibilityOfChildrenOfEntity: (id, hide) => {
		const relevantEntity = state.availableEntities.find(
			(entity) => entity.id === id
		);

		if (!relevantEntity) {
			return;
		}

		relevantEntity.hideChildren = hide;
		state.availableEntities = [...state.availableEntities];
	}
};

export default mutations;
